// ================================================================
// Senden-Dialog — Dokument per E-Mail ODER WhatsApp verschicken.
// ================================================================
// Herausgelöst aus CompanyDashboard.js (Datei-Split, Runde 1 — 2026-07-19).
// Gleicher Injektions-Ansatz wie documentsView.js: geteilter State `s` +
// draw/loadWaInstances kommen einmal via initSendModal() — loadWaInstances
// lebt (noch) im WhatsApp-Teil von CompanyDashboard.
import { t } from '../../i18n/index.js';
import { icon, escapeHtml } from '../../utils/dom.js';
import { getWhatsAppChats } from '../../core/whatsapp.js';
import { sendDocumentEmail, sendDocumentWhatsApp, getWhatsappShareLink, warmDocumentPreviewCard, getDocument } from '../../core/documents.js';
import { splitPhone } from '../../react/lib/countryCodes.ts';
import { WHATSAPP_ENABLED } from '../../core/features.js';
import { loadDocuments } from './documentsView.js';
import { setSendSheetOpen } from '../../core/uiCommands.ts';
import { getTurnKey } from '../../core/assistantTurn.ts';

const WA_COUNTRIES = [
  { code: '41', flag: '🇨🇭', name: 'Schweiz' },
  { code: '49', flag: '🇩🇪', name: 'Deutschland' },
  { code: '43', flag: '🇦🇹', name: 'Österreich' },
  { code: '33', flag: '🇫🇷', name: 'Frankreich' },
  { code: '39', flag: '🇮🇹', name: 'Italien' },
  { code: '98', flag: '🇮🇷', name: 'Iran' },
];

let s;
let draw;
let loadWaInstances;

/** Einmal beim Modul-Start von CompanyDashboard aufrufen. */
export function initSendModal(ctx) {
  ({ s, draw, loadWaInstances } = ctx);
}

/**
 * @param docFromCaller optional: das Dokument-Objekt des Aufrufers (z.B. die
 * React-Dokumente-Insel, die ihre eigene Liste hält). Hat Vorrang vor dem
 * s.docs-Nachschlag — der Shell-Cache kann leer oder veraltet sein.
 * Rückwärtskompatibel: alle bisherigen Aufrufer (id, number) bleiben gültig.
 */
export function openSendModal(id, number, docFromCaller = null) {
  if (!number && docFromCaller) number = docFromCaller.number || '';
  s.sendModal = { id, number, to: '', subject: '', message: '' };
  s.sendError = ''; s.sendOk = false; s.sendBusy = false;
  s.sendTab = 'email';
  s.sendWaMode = 'chat'; s.sendWaJid = ''; s.sendWaNumber = ''; s.sendWaCaption = '';
  s.sendWaChats = []; s.sendWaChatsLoading = false; s.sendWaWebUrl = '';
  s.sendWaInstance = s.waActiveInstance || (s.waInstances[0] && s.waInstances[0].id) || null;
  s.sendCardBusy = true;
  // Merken, seit WELCHER Gesprächsrunde dieser Dialog zu sehen ist. Der
  // Server lässt ein «jetzt senden» des Assistenten nur zu, wenn das eine
  // FRÜHERE Runde war — sonst hat der Nutzer den Empfänger nie gesehen
  // (backend/.../open-send-sheet.tool.ts).
  setSendSheetOpen(id, getTurnKey());
  draw();
  ensureWaForSend();
  warmPreviewCard(id);
  prefillRecipient(id);
}

/**
 * Empfänger aus der Kundenkarte vorbefüllen — genau wie in der Handy-App
 * (MobileOfferSheet): E-Mail-Feld und WhatsApp-Nummer stehen schon da, der
 * Nutzer (oder der Assistent) muss nur noch senden. Vorher startete der
 * Dialog am Desktop LEER, und jede Offerte kostete Abtippen.
 *
 * Was der Nutzer bereits getippt hat, wird nie überschrieben; ein Fehlschlag
 * ist unkritisch — dann bleibt es beim leeren Feld wie bisher.
 */
let prefillLaeuft = Promise.resolve();
function prefillRecipient(id) {
  prefillLaeuft = ladeEmpfaenger(id);
  return prefillLaeuft;
}
async function ladeEmpfaenger(id) {
  try {
    const r = await getDocument(id);
    const contact = r && r.contact;
    if (!s.sendModal || s.sendModal.id !== id) return; // Dialog schon weiter
    // Nummer nachtragen, wenn der Dialog ohne sie geöffnet wurde (Voice-Weg).
    if (!s.sendModal.number && r && r.document) s.sendModal.number = r.document.number || '';
    if (!contact) { draw(); return; }
    if (contact.email && !s.sendModal.to) s.sendModal.to = contact.email;
    if (contact.phone && !s.sendWaNumber) {
      const p = splitPhone(contact.phone);
      // Vorwahl nur setzen, wenn dieser Dialog sie überhaupt anbietet — sonst
      // bleibt die Schweiz stehen und die Nummer geht international rein
      // (normalizeWaNumber lässt eine Nummer mit Landesvorwahl unangetastet).
      if (WA_COUNTRIES.some((c) => c.code === p.dial)) {
        s.sendWaCountry = p.dial;
        s.sendWaNumber = p.national;
      } else {
        s.sendWaNumber = p.dial + p.national;
      }
      // Mit bekannter Nummer ist «Nummer eingeben» der fertige Weg — die
      // Chat-Liste wäre ein zusätzlicher Suchschritt für dasselbe Ziel.
      s.sendWaMode = 'manual';
    }
    draw();
  } catch { /* ohne Vorbefüllung weiter — der Dialog funktioniert auch leer */ }
}

// Die Link-Vorschaukarte vorbereiten, solange der Nutzer Empfänger und Text
// prüft. Ohne das entstünde sie erst, wenn der Vorschau-Roboter von WhatsApp
// den Link abholt — also genau dann, wenn die Nachricht beim Kunden ankommt;
// das dauert auf dem Server 3–5 Sekunden, und wartet der Roboter nicht, sieht
// der Kunde nie eine Karte (docs/link-vorschau.md).
//
// Der Wecker ist der wichtigere Teil: hängt oder scheitert das Rendern, wird
// nach 10 Sekunden trotzdem freigegeben. Eine fehlende Vorschau ist ein
// Schönheitsfehler — eine Offerte, die sich nicht senden lässt, nicht.
function warmPreviewCard(id) {
  let fertig = false;
  const freigeben = () => {
    if (fertig) return;
    fertig = true;
    // Nur, wenn der Dialog noch offen ist UND noch dasselbe Dokument zeigt:
    // sonst würde ein spät eintreffender Aufruf einen fremden Knopf entsperren.
    if (s.sendModal && s.sendModal.id === id) { s.sendCardBusy = false; draw(); }
  };
  const wecker = setTimeout(freigeben, 10000);
  warmDocumentPreviewCard(id)
    .catch(() => undefined)
    .then(() => { clearTimeout(wecker); freigeben(); });
}

// اطمینان از بارگیری شماره‌های واتساپ حتی اگر کاربر وارد منوی WhatsApp نشده باشد
async function ensureWaForSend() {
  if (!s.waInstances || s.waInstances.length === 0) {
    try { await loadWaInstances(); } catch { /* بی‌خیال */ }
    s.sendWaInstance = s.waActiveInstance || (s.waInstances[0] && s.waInstances[0].id) || null;
  }
  if (s.sendModal) { draw(); loadSendWaChats(); }
}

async function loadSendWaChats() {
  if (!s.sendWaInstance) return;
  s.sendWaChatsLoading = true; draw();
  try {
    const r = await getWhatsAppChats(s.sendWaInstance);
    s.sendWaChats = (r && r.chats) ? r.chats : (Array.isArray(r) ? r : []);
  } catch { s.sendWaChats = []; }
  s.sendWaChatsLoading = false; draw();
}

export function setSendTab(tab) { s.sendTab = tab; s.sendError = ''; draw(); }
export function setSendWaMode(mode) { s.sendWaMode = mode; s.sendError = ''; draw(); }

export function toggleCcDropdown() { s.sendCcOpen = !s.sendCcOpen; draw(); }
export function selectCc(code) { s.sendWaCountry = code; s.sendCcOpen = false; draw(); }

/**
 * Voice bedient den Senden-Dialog (open_send_sheet → ui_command SEND_SHEET) —
 * das Desktop-Gegenstück zum Handy-Sheet: öffnen, Kanal wählen, Empfänger
 * überschreiben, `send` drückt «Jetzt senden». Ohne diesen Handler passierte
 * am Desktop auf «schick es» GAR NICHTS (der Assistent meldete «geöffnet»,
 * der Bildschirm blieb stehen).
 */
export async function applySendSheetCommand(cmd) {
  const id = cmd.documentId;
  if (!id) return;
  if (!s.sendModal || s.sendModal.id !== id) {
    const bekannt = (s.docs || []).find((d) => d.id === id);
    openSendModal(id, bekannt ? bekannt.number : '');
  }
  if (cmd.channel === 'email' || cmd.channel === 'whatsapp') s.sendTab = cmd.channel;
  if (cmd.toEmail) s.sendModal.to = cmd.toEmail;
  if (cmd.phone) {
    const p = splitPhone(cmd.phone);
    if (WA_COUNTRIES.some((c) => c.code === p.dial)) { s.sendWaCountry = p.dial; s.sendWaNumber = p.national; }
    else s.sendWaNumber = p.dial + p.national;
    s.sendWaMode = 'manual';
  }
  s.sendError = '';
  draw();
  if (!cmd.send) return;
  // Erst senden, wenn der hinterlegte Empfänger da ist: sagt der Nutzer in
  // EINEM Zug «per E-Mail schicken», wird der Dialog gerade erst geöffnet und
  // das Feld wäre noch leer — der Versand liefe in «bitte gültige E-Mail».
  await prefillLaeuft;
  await submitSendModal();
}

export function closeSendModal() {
  s.sendModal = null; s.sendError = ''; s.sendOk = false; s.sendWaWebUrl = '';
  setSendSheetOpen(null, null);
  draw();
}

export function sendModalInput(field, value) {
  if (s.sendModal) s.sendModal[field] = value;
}

/**
 * WhatsApp öffnen — die APP, nicht der Umweg über die Web-Seite.
 *
 * Der Server liefert eine api.whatsapp.com-Adresse; am Handy öffnet die
 * automatisch die App, am Rechner landete man dagegen in WhatsApp Web (und
 * oft nicht einmal dort: `window.open` NACH einem `await` läuft nicht mehr in
 * der Klick-Geste und der Popup-Blocker verschluckte es lautlos — genau der
 * Grund, warum am Desktop scheinbar «nichts passierte»).
 *
 * Darum hier die App-Adresse `whatsapp://send?…` als normale Navigation: kein
 * Popup-Blocker, das Dashboard bleibt stehen, das Betriebssystem reicht an die
 * installierte App weiter. Ob das geklappt hat, verrät der Fokusverlust des
 * Fensters — kommt keiner, gibt der Aufrufer den Web-Link als Rückweg frei.
 */
function oeffneWhatsappApp(webUrl) {
  let appUrl = webUrl;
  try {
    const u = new URL(webUrl);
    const phone = u.searchParams.get('phone') || '';
    const text = u.searchParams.get('text') || '';
    appUrl = `whatsapp://send?phone=${encodeURIComponent(phone)}&text=${encodeURIComponent(text)}`;
  } catch { /* unerwartete Form — dann eben die Adresse, wie sie kam */ }
  return new Promise((fertig) => {
    let weg = false;
    const merker = () => { weg = true; };
    window.addEventListener('blur', merker, { once: true });
    try { window.location.href = appUrl; } catch { /* Schema nicht erlaubt */ }
    setTimeout(() => { window.removeEventListener('blur', merker); fertig(weg); }, 1400);
  });
}

function normalizeWaNumber(raw, countryCode) {
  let n = (raw || '').replace(/[^0-9]/g, '');
  if (!n) return '';
  // اگر با 00 شروع شد (فرمت بین‌المللی) → حذف 00
  if (n.startsWith('00')) n = n.slice(2);
  // اگر با 0 شروع شد (شماره‌ی محلی) → حذف 0 و افزودن کد کشور
  else if (n.startsWith('0')) n = countryCode + n.slice(1);
  // اگر از قبل با کد کشور شروع شده → دست‌نخورده
  else if (!n.startsWith(countryCode)) n = countryCode + n;
  return n;
}

export async function submitSendModal() {
  if (!s.sendModal) return;
  const { id } = s.sendModal;

  if (s.sendTab === 'whatsapp') {
    // مقصد: یا jid از چت، یا شماره‌ی دستی
    // WhatsApp pausiert: statt die Datei über den Server zu schicken, öffnen
    // wir WhatsApp mit fertiger Nachricht + Link auf die Dokument-Seite. Der
    // Nutzer drückt in WhatsApp selbst auf Senden.
    if (!WHATSAPP_ENABLED) {
      const num = normalizeWaNumber(s.sendWaNumber, s.sendWaCountry);
      if (num.length < 10) { s.sendError = t('dashboard.sendModal.errValidNumber'); draw(); return; }
      s.sendBusy = true; s.sendError = ''; draw();
      try {
        const share = await getWhatsappShareLink(id, num);
        s.sendWaWebUrl = share.url; // macht den Hinweis unten zum Rückweg
        s.sendOk = true; s.sendBusy = false; draw();
        s.docsLoaded = false; loadDocuments();
        const geoeffnet = await oeffneWhatsappApp(share.url);
        // Nur schliessen, wenn WhatsApp wirklich aufgegangen ist. Sonst bleibt
        // der Dialog stehen — mit dem anklickbaren Hinweis als Rückweg.
        if (geoeffnet) setTimeout(() => { closeSendModal(); }, 1200);
      } catch (err) {
        s.sendError = (err && err.message) ? err.message : t('dashboard.sendModal.errSend');
        s.sendBusy = false; draw();
      }
      return;
    }

    let jid = '';
    if (s.sendWaMode === 'chat') {
      if (!s.sendWaJid) { s.sendError = t('dashboard.sendModal.errChooseChat'); draw(); return; }
      jid = s.sendWaJid;
    } else {
      const num = normalizeWaNumber(s.sendWaNumber, s.sendWaCountry);
      if (num.length < 10) { s.sendError = t('dashboard.sendModal.errValidNumber'); draw(); return; }
      jid = num + '@s.whatsapp.net';
    }
    if (!s.sendWaInstance) { s.sendError = t('dashboard.sendModal.errNoWaNumber'); draw(); return; }
    s.sendBusy = true; s.sendError = ''; draw();
    try {
      await sendDocumentWhatsApp(id, { instanceId: s.sendWaInstance, remoteJid: jid, caption: s.sendWaCaption });
      s.sendOk = true; s.sendBusy = false; draw();
      s.docsLoaded = false; loadDocuments(); // bad az ersal Rechnung lock mishavad -> list refresh
      setTimeout(() => { closeSendModal(); }, 1500);
    } catch (err) {
      s.sendError = (err && err.message) ? err.message : t('dashboard.sendModal.errSend');
      s.sendBusy = false; draw();
    }
    return;
  }

  // تب ایمیل
  const { to, subject, message } = s.sendModal;
  if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to.trim())) {
    s.sendError = t('dashboard.sendModal.errValidEmail'); draw(); return;
  }
  s.sendBusy = true; s.sendError = ''; draw();
  try {
    await sendDocumentEmail(id, { to: to.trim(), subject, message });
    s.sendOk = true; s.sendBusy = false; draw();
    s.docsLoaded = false; loadDocuments(); // bad az ersal Rechnung lock mishavad -> list refresh
    setTimeout(() => { closeSendModal(); }, 1500);
  } catch (err) {
    s.sendError = (err && err.message) ? err.message : t('dashboard.sendModal.errSend');
    s.sendBusy = false; draw();
  }
}

// ── Overlay-Renderer (wird von draw() am Ende an den Container gehängt) ──
// modal ارسال سند — دو تب: E-Mail و WhatsApp
export function renderSendModal(container) {
  if (!s.sendModal) return;
  const m = s.sendModal;
  const ov = document.createElement('div');
  ov.className = 'cd-send-modal';

  // Im Link-Modus hat NICHT die App gesendet, sondern WhatsApp wurde geöffnet
  // — «Per WhatsApp gesendet» wäre eine Behauptung über etwas, das erst der
  // Nutzer tut. Gleiche Formulierung wie in der Handy-App.
  const waOk = WHATSAPP_ENABLED
    ? t('dashboard.sendModal.sentViaWhatsapp')
    : t('leadMobile.offer.waOpenedOk', { to: escapeHtml('+' + s.sendWaCountry + ' ' + (s.sendWaNumber || '').trim()) });
  const okText = s.sendTab === 'whatsapp' ? waOk : t('dashboard.sendModal.sentTo', { to: escapeHtml(m.to) });
  const okBox = s.sendOk ? '<div class="cd-ok">' + icon('check', 16) + okText + '</div>' : '';
  const errBox = s.sendError ? '<div class="cd-form-err">' + escapeHtml(s.sendError) + '</div>' : '';

  // محتوای تب ایمیل
  const emailPane =
    '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.recipientEmail')) + '</label>' +
    '<input class="cd-send-input" id="send-to" type="email" placeholder="kunde@example.ch" value="' + escapeHtml(m.to) + '" data-send-field="to" />' +
    '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.subject')) + '</label>' +
    '<input class="cd-send-input" type="text" placeholder="' + escapeHtml(t('dashboard.sendModal.automatic')) + '" value="' + escapeHtml(m.subject) + '" data-send-field="subject" />' +
    '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.message')) + '</label>' +
    '<textarea class="cd-send-input cd-send-area" rows="3" placeholder="' + escapeHtml(t('dashboard.sendModal.automaticText')) + '" data-send-field="message">' + escapeHtml(m.message) + '</textarea>';

  // محتوای تب واتساپ
  let waPane = '';
  if (!WHATSAPP_ENABLED) {
    // WhatsApp pausiert: keine Instanz, keine Chat-Liste, kein Datei-Versand.
    // Nur die Nummer — der Rest passiert in WhatsApp selbst. Der Hinweis sagt
    // klar, was gleich geschieht, statt «gesendet» zu versprechen.
    const curC = WA_COUNTRIES.find((c) => c.code === s.sendWaCountry) || WA_COUNTRIES[0];
    const ccList = s.sendCcOpen
      ? '<div class="cd-cc-backdrop" data-action="toggle-cc"></div>' +
        '<div class="cd-cc-list">' + WA_COUNTRIES.map((c) =>
          '<button type="button" class="cd-cc-item' + (c.code === s.sendWaCountry ? ' active' : '') + '" data-action="select-cc" data-code="' + c.code + '">' +
            '<span class="cd-cc-flag">' + c.flag + '</span><span>+' + c.code + '</span>' +
          '</button>'
        ).join('') + '</div>'
      : '';
    waPane =
      '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.waNumber')) + '</label>' +
      '<div class="cd-send-phone-row">' +
        '<div class="cd-cc-dropdown">' +
          '<button type="button" class="cd-cc-trigger" data-action="toggle-cc">' +
            '<span class="cd-cc-flag">' + curC.flag + '</span><span>+' + curC.code + '</span>' +
            '<span class="cd-cc-arrow">' + icon('expand_more', 16) + '</span>' +
          '</button>' + ccList +
        '</div>' +
        '<input class="cd-send-input" type="text" placeholder="79 123 45 67" value="' + escapeHtml(s.sendWaNumber) + '" data-send-wa-field="sendWaNumber" />' +
      '</div>' +
      // Nach dem Senden ist der Hinweis der RÜCKWEG: hat der Rechner die
      // WhatsApp-App nicht (oder blockt er den Sprung), führt ein Klick hier
      // in WhatsApp Web — mit derselben fertigen Nachricht.
      '<div class="cd-form-hint">' + (s.sendWaWebUrl
        ? '<a href="' + escapeHtml(s.sendWaWebUrl) + '" target="_blank" rel="noopener">' + escapeHtml(t('dashboard.sendModal.waLinkHint')) + '</a>'
        : escapeHtml(t('dashboard.sendModal.waLinkHint'))) + '</div>';
  } else if (!s.sendWaInstance) {
    waPane = '<div class="cd-send-empty">' + escapeHtml(t('dashboard.sendModal.noWaNumber')) + '</div>';
  } else {
    // انتخابگر حالت مقصد
    const modeTabs =
      '<div class="cd-send-mode">' +
        '<button class="cd-send-mode-btn ' + (s.sendWaMode === 'chat' ? 'active' : '') + '" data-action="send-wa-mode-chat">' + escapeHtml(t('dashboard.sendModal.fromChats')) + '</button>' +
        '<button class="cd-send-mode-btn ' + (s.sendWaMode === 'manual' ? 'active' : '') + '" data-action="send-wa-mode-manual">' + escapeHtml(t('dashboard.sendModal.enterNumber')) + '</button>' +
      '</div>';

    let dest = '';
    if (s.sendWaMode === 'chat') {
      if (s.sendWaChatsLoading) {
        dest = '<div class="cd-send-empty">' + escapeHtml(t('dashboard.sendModal.chatsLoading')) + '</div>';
      } else if (!s.sendWaChats.length) {
        dest = '<div class="cd-send-empty">' + escapeHtml(t('dashboard.sendModal.noChatsFound')) + '</div>';
      } else {
        const opts = s.sendWaChats.map((c) => {
          const jid = c.remoteJid || c.jid || '';
          const name = c.name || c.pushName || jid.split('@')[0];
          const sel = jid === s.sendWaJid ? ' selected' : '';
          return '<option value="' + escapeHtml(jid) + '"' + sel + '>' + escapeHtml(name) + '</option>';
        }).join('');
        dest = '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.chooseChat')) + '</label>' +
          '<select class="cd-send-input" data-send-wa-field="sendWaJid"><option value="">' + escapeHtml(t('dashboard.sendModal.choosePlaceholder')) + '</option>' + opts + '</select>';
      }
    } else {
      const curC = WA_COUNTRIES.find((c) => c.code === s.sendWaCountry) || WA_COUNTRIES[0];
      const ccList = s.sendCcOpen
        ? '<div class="cd-cc-backdrop" data-action="toggle-cc"></div>' +
          '<div class="cd-cc-list">' + WA_COUNTRIES.map((c) =>
            '<button type="button" class="cd-cc-item' + (c.code === s.sendWaCountry ? ' active' : '') + '" data-action="select-cc" data-code="' + c.code + '">' +
              '<span class="cd-cc-flag">' + c.flag + '</span><span>+' + c.code + '</span>' +
            '</button>'
          ).join('') + '</div>'
        : '';
      dest = '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.waNumber')) + '</label>' +
        '<div class="cd-send-phone-row">' +
          '<div class="cd-cc-dropdown">' +
            '<button type="button" class="cd-cc-trigger" data-action="toggle-cc">' +
              '<span class="cd-cc-flag">' + curC.flag + '</span><span>+' + curC.code + '</span>' +
              '<span class="cd-cc-arrow">' + icon('expand_more', 16) + '</span>' +
            '</button>' + ccList +
          '</div>' +
          '<input class="cd-send-input" type="text" placeholder="79 123 45 67" value="' + escapeHtml(s.sendWaNumber) + '" data-send-wa-field="sendWaNumber" />' +
        '</div>' +
        '<div class="cd-form-hint">' + escapeHtml(t('dashboard.sendModal.numberHint')) + '</div>';
    }

    const caption =
      '<label class="cd-send-label">' + escapeHtml(t('dashboard.sendModal.messageOptional')) + '</label>' +
      '<textarea class="cd-send-input cd-send-area" rows="2" placeholder="' + escapeHtml(t('dashboard.sendModal.captionPlaceholder')) + '" data-send-wa-field="sendWaCaption">' + escapeHtml(s.sendWaCaption) + '</textarea>';

    waPane = modeTabs + dest + caption;
  }

  const activePane = s.sendTab === 'whatsapp' ? waPane : emailPane;

  ov.innerHTML =
    '<div class="cd-send-backdrop" data-action="close-send"></div>' +
    '<div class="cd-send-card">' +
      '<div class="cd-send-head">' +
        '<h3>' + escapeHtml(t('dashboard.sendModal.sendTitle', { number: m.number })) + '</h3>' +
        '<button class="cd-bell" data-action="close-send">' + icon('close', 18) + '</button>' +
      '</div>' +
      '<div class="cd-send-tabs">' +
        '<button class="cd-send-tab ' + (s.sendTab === 'email' ? 'active' : '') + '" data-action="send-tab-email">' + icon('mail', 16) + escapeHtml(t('dashboard.nav.email')) + '</button>' +
        '<button class="cd-send-tab ' + (s.sendTab === 'whatsapp' ? 'active' : '') + '" data-action="send-tab-whatsapp">' + icon('chat', 16) + escapeHtml(t('dashboard.nav.whatsapp')) + '</button>' +
      '</div>' +
      okBox + errBox +
      activePane +
      '<div class="cd-send-foot">' +
        '<button class="cd-btn-ghost" data-action="close-send">' + escapeHtml(t('common.cancel')) + '</button>' +
        // Drei Zustände: Vorschau wird vorbereitet → gesperrt mit Spinner;
        // gesendet wird → gesperrt mit Spinner; sonst normal.
        '<button class="cd-btn-primary" data-action="submit-send"' +
          ((s.sendBusy || s.sendCardBusy) ? ' disabled' : '') + '>' +
          ((s.sendBusy || s.sendCardBusy) ? '<span class="cd-mini-spin"></span> ' : '') +
          escapeHtml(
            s.sendBusy ? t('dashboard.sendModal.sending')
              : s.sendCardBusy ? t('dashboard.sendModal.preparing')
                : t('dashboard.sendModal.send'),
          ) +
        '</button>' +
      '</div>' +
    '</div>';
  container.appendChild(ov);
}
